import React, { useState } from "react";
import PortalRoot from "../../portal/PortalRoot";
import { OverlayOpacity } from "../../portal/types";
import { useLayout } from "../../layout/LayoutContext";
import { showToast } from "../../../toast/toast";
import { PlaydateInvite, useDeletePlaydateInviteMutation } from "../../../gql/types";
import PlaydateInviteOrganizerCancellationView from "../view/PlaydateInviteOrganizerCancellationView";
import { useToastContext } from "contexts/ToastContext";

interface CancellationWorkflowProps {
    inviteID: string;
    onBack: () => void;
    onDataChange: (success: boolean, updatedInvite?: PlaydateInvite) => void;
    baseZIndex?: number;
    /**
     * Optional override for cancel logic (e.g. Storybook or mocking)
     */
    onCancel?: (message: string) => Promise<void>;
}

const PlaydateInviteOrganizerCancellationWorkflow: React.FC<CancellationWorkflowProps> = ({
    inviteID,
    onBack,
    onDataChange,
    baseZIndex,
    onCancel,
}) => {
    const [loading, setLoading] = useState(false);
    const [deleteInvite] = useDeletePlaydateInviteMutation();
    const { baseZIndex: layoutZIndex } = useLayout();
    const { pushToastEvent } = useToastContext();

    const finalZIndex = baseZIndex ?? layoutZIndex;

    const handleSubmitCancel = async (message: string) => {
        setLoading(true);
        try {
            let updatedInvite: PlaydateInvite | null = null;
            if (onCancel) {
                await onCancel(message);
            } else {
                const { data } = await deleteInvite({
                    variables: {
                        inviteID,
                        cancellationMessage: message,
                    },
                });
                if (data?.deletePlaydateInvite) {
                    updatedInvite = data.deletePlaydateInvite;
                }
            }
            pushToastEvent({
                eventType: "success",
                message: "Invite Cancelled Successfully.",
            })
            onDataChange(true, updatedInvite !== null ? updatedInvite : undefined);
        } catch (error: any) {
            console.log(error);
            showToast("error", "Failed to cancel the invite. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <PortalRoot baseZIndex={finalZIndex} overlayOpacity={OverlayOpacity.COVERED}>
            <PlaydateInviteOrganizerCancellationView
                baseZIndex={finalZIndex}
                onBack={onBack}
                onSubmitCancel={handleSubmitCancel}
                loading={loading}
            />
        </PortalRoot>
    );
};

export default PlaydateInviteOrganizerCancellationWorkflow;
